<template>
  <div class="config-tab tab-content">
    <div v-if="cardsToLinks && cardsToLinks.length > 1" class="resize-wrapper">
      <VueDraggableResizable
        :draggable="false"
        :active="true"
        :prevent-deactivation="true"
        :w="204"
        :minWidth="90"
        :handles="['mr']"
        axis="x"
      >
        <sideNav :links="cardsToLinks" />
      </VueDraggableResizable>
    </div>
    <div class="content" v-if="cards" v-scroll:#tab-content="onScroll">
      <card
        v-for="card in visibleCards"
        :key="card.id"
        :title="card.name"
        :id="card.id"
        :tooltip="card.tooltip"
        :helpContent="card.helpContent"
      >
        <fields
          :rows="card.fields"
          :category="computedCategory(card)"
          :id="id"
          :isTemplate="isTemplate"
        />
      </card>
    </div>
  </div>
</template>

<script>
/**
 * @fileoverview Config tab
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import { mapGetters } from "vuex";
import VueDraggableResizable from "vue-draggable-resizable";

import sideNav from "@/qux-common/qux22/components/organisms/side-navigation";

import card from "@/components/card";

import fields from "./fields";

import visibleCards from "@/util/visibleCards";

export default {
  name: "ConfigTab",
  components: { VueDraggableResizable, card, sideNav, fields },
  props: {
    id: {
      type: String,
    },
    tab: {
      type: String,
    },
    isTemplate: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isLoadingTemplate() {
      return this.pendingCalls.includes("/client/template/retrieve");
    },
    configData() {
      return () => import(`@/components/client/configs/${this.tab}`);
    },
    cardsToLinks() {
      if (!this.cards) return;
      return this.getVisibleCards(this.cards).map((card) => ({
        name: card.name,
        id: card.id,
      }));
    },
    fieldList() {
      const fieldData =
        this.configTemplate &&
        this.category &&
        this.configTemplate[this.category];
      return fieldData && fieldData.AvailableFieldList;
    },
    visibleCards() {
      return this.getVisibleCards(this.cards);
    },
    ...mapGetters({
      mergedConfig: "getMergedConfig",
      configTemplate: "getConfigTemplate",
      pendingCalls: "getPendingCallList",
      configVisibility: "getConfigVisibility",
      debugMode: "getDebugMode",
    }),
  },
  asyncComputed: {
    async cards() {
      const configData = await this.configData();
      return configData.cards;
    },
    async category() {
      const configData = await this.configData();
      return configData.category;
    },
  },
  methods: {
    checkForMissingConfigFields() {
      if (!this.debugMode || !this.fieldList || !this.cards) return;

      // find all fields in config data
      const getConfigIds = (fieldList) => {
        const fieldIds = [];
        for (const field of fieldList) {
          if (field.FieldId) fieldIds.push(field.FieldId);

          if (field.AvailableFieldList) {
            const subField = getConfigIds(field.AvailableFieldList);
            if (subField) fieldIds.push(subField);
          }

          if (field.Options) {
            for (const option of field.Options) {
              if (option.AvailableFieldList) {
                const subField = getConfigIds(option.AvailableFieldList);
                if (subField) fieldIds.push(subField);
              }
            }
          }
        }
        // return only unique id's
        return [...new Set(fieldIds.flat())];
      };
      const configIds = getConfigIds(this.fieldList);

      // find all fields in the card
      const getFieldIds = (fields) => {
        const fieldIds = [];
        if (fields.id) fieldIds.push(fields.id);
        if (fields.items) {
          for (const item of fields.items) {
            if (item.id) {
              fieldIds.push(item.id);
            }
          }
        }
        if (fields.layout) {
          for (const l of fields.layout) {
            const subField = getFieldIds(l);
            if (subField) fieldIds.push(subField);
          }
        }
        // listItems doesn't serve any purpose other than to inform this check that the fields are being used in the list
        if (fields.listItems) fieldIds.push(fields.listItems);
        if (Array.isArray(fields)) {
          for (const l of fields) {
            const subField = getFieldIds(l);
            if (subField) fieldIds.push(subField);
          }
        }
        // return only unique id's
        return [...new Set(fieldIds.flat())];
      };
      const getFieldIdsFromCards = (cards) => {
        const fieldIds = [];
        // eslint-disable-next-line no-unused-vars
        for (const card of cards) {
          fieldIds.push(getFieldIds(card.fields));
        }
        // return only unique id's
        return [...new Set(fieldIds.flat())];
      };
      const fieldIds = getFieldIdsFromCards(this.cards);
      // check which ones are missing in card
      const cardMissingFields = configIds.filter(
        (id) => !fieldIds.includes(id)
      );
      if (cardMissingFields.length) {
        console.warn(
          `Some returned template fields are missing from the layout:${cardMissingFields.map(
            (field) => `\n• ${field}`
          )}`
        );
      }
      return cardMissingFields;
    },
    computedCategory(currentCard) {
      if (currentCard.category) {
        return currentCard.category;
      } else {
        return this.category;
      }
    },
    getVisibleCards(cardsToFilter) {
      return visibleCards({
        cardsToFilter,
        category: this.category,
        configVisibility: this.configVisibility,
        id: this.id,
        hidePersonalDetail: this.isTemplate,
      });
    },
    onScroll() {
      if (!this.cards) return;
      for (const card of this.cards) {
        //range for card being the active card
        const minTop = 220;
        const maxTop = 360;

        const el = document.getElementById(card.id);
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        if (top > minTop && top < maxTop) {
          this.$router.replace({ hash: `#${card.id}` });
        }
      }
    },
  },
  mounted() {
    if (!this.isLoadingTemplate) this.checkForMissingConfigFields();
  },
  updated() {
    this.$emit("updated");
  },
  watch: {
    isLoadingTemplate() {
      if (!this.isLoadingTemplate) this.checkForMissingConfigFields();
    },
  },
};
</script>

<style lang="scss" scoped>
.config-tab {
  display: flex;
  .resizable {
    height: 100% !important; // VueDraggableResizable doesn't allow setting this as a height
  }
  &::v-deep {
    .handle {
      box-sizing: border-box;
      position: absolute;
      width: 10px;
      height: 100%;
      top: -$qvo-spacing-24;
    }
    .handle-mr {
      right: 0;
      cursor: e-resize;
      z-index: 1;
    }
  }
}
.content {
  width: 100%;
  margin: $qvo-spacing-24;
}
.card {
  margin-bottom: $qvo-spacing-24;
}
nav {
  padding-top: 0;
  position: sticky;
  top: $qvo-spacing-24;
}
.resize-wrapper {
  padding-top: $qvo-spacing-24;
  background: $qvo-color-blue-100;
}
</style>